/**
 * SidebarChatTab - Chat tab content for VideoSidebar
 * All messages are persisted via Convex (waitingRoomMessages table),
 * shared between internal app video sidebar and client waiting room.
 */

import { useRef, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { Button, Input, ScrollArea } from "@jourfx/ui";
import { Send, Paperclip } from "lucide-react";
import { useDateFormat } from "@/hooks/useDateFormat";

interface SidebarChatTabProps {
  appointmentId: string;
}

type Id<T extends string> = string & { __tableName: T };

export function SidebarChatTab({ appointmentId }: SidebarChatTabProps) {
  const { t } = useTranslation("video");
  const { formatShortTime } = useDateFormat();
  const [inputValue, setInputValue] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const apiRef = api as any;

  const waitingRoomMessages = useQuery(
    apiRef.waitingRoomChat.index.listForStaff,
    { appointmentId: appointmentId as Id<"appointments"> }
  ) as Array<{
    _id: string;
    senderType: "versicherter" | "staff";
    senderName: string;
    message: string;
    createdAt: number;
  }> | undefined;

  const sendAsStaff = useMutation(apiRef.waitingRoomChat.index.sendAsStaff);

  const sortedMessages = useMemo(
    () => [...(waitingRoomMessages || [])].sort((a, b) => a.createdAt - b.createdAt),
    [waitingRoomMessages]
  );

  const sendMessage = useCallback(async () => {
    if (!inputValue.trim()) return;

    const message = inputValue.trim();
    setInputValue("");

    try {
      await sendAsStaff({
        appointmentId: appointmentId as Id<"appointments">,
        message,
      });
    } catch (err) {
      console.error("Failed to send chat message:", err);
    }
  }, [inputValue, sendAsStaff, appointmentId]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="flex flex-col h-full">
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-3" role="log" aria-label={t("chat.ariaLabel")}>
          {sortedMessages.length === 0 && (
            <p className="text-center text-muted-foreground text-sm py-8">
              {t("chat.noMessages")}
            </p>
          )}

          {sortedMessages.map((msg) => (
            <div
              key={msg._id}
              className={`flex ${msg.senderType === "staff" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] rounded-lg px-3 py-2 ${
                  msg.senderType === "staff"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground"
                }`}
              >
                <p className="text-xs font-medium mb-0.5 opacity-70">{msg.senderName}</p>
                <p className="text-sm">{msg.message}</p>
                <p
                  className={`text-xs mt-1 ${
                    msg.senderType === "staff"
                      ? "text-primary-foreground/70"
                      : "text-muted-foreground"
                  }`}
                >
                  {formatShortTime(msg.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      {/* Chat Input */}
      <div className="flex-shrink-0 border-t border-border p-3">
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 shrink-0"
            disabled
            aria-label={t("chat.attachFile")}
          >
            <Paperclip className="w-4 h-4" />
          </Button>
          <Input
            placeholder={t("chat.placeholder")}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            className="flex-1"
          />
          <Button
            size="icon"
            onClick={sendMessage}
            disabled={!inputValue.trim()}
            className="h-10 w-10 shrink-0"
            aria-label={t("chat.send")}
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
